import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import './CSS/FullMenu.css';
import alidarbar from "../Images/Header/Redfulllogo.png";

// Models defined directly in the same file
class MenuCategory {
  constructor(data = {}) {
    this.id = data.id || '';
    this.name = data.name || '';
    this.display_name = data.display_name || '';
    this.description = data.description || '';
    this.items = Array.isArray(data.items) 
      ? data.items.map(item => new MenuItem(item)) 
      : [];
    this.order_index = data.order_index || 0;
    this.is_active = data.is_active ?? true;
  }

  get itemCount() {
    return this.items.length;
  }

  get availableItems() {
    return this.items.filter(item => item.is_available);
  }

  get icon() {
    const name = this.name.toLowerCase();
    if (name.includes('starter') || name.includes('appetizer')) return '🥗';
    if (name.includes('main') || name.includes('course')) return '🍛';
    if (name.includes('dessert') || name.includes('sweet')) return '🍰';
    if (name.includes('bread') || name.includes('naan')) return '🍞';
    if (name.includes('rice') || name.includes('biryani')) return '🍚';
    if (name.includes('street') || name.includes('fast')) return '🌮';
    if (name.includes('drink') || name.includes('beverage')) return '🥤';
    return '🍽️';
  }
}

class MenuItem {
  constructor(data = {}) {
    this.id = data.id || `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.name = data.name || 'Unnamed Item';
    this.description = data.description || '';
    this.detailed_description = data.detailed_description || '';
    this.image = data.image || '';
    this.is_available = data.is_available ?? true;
    this.is_chef_special = data.is_chef_special ?? false;
    this.is_popular = data.is_popular ?? false;
    this.cooking_time = data.cooking_time || '15-20';
    this.spice_level = data.spice_level || 'medium';
    this.food_type_name = data.food_type_name || 'Main Course';
    this.category_name = data.category_name || '';
    this.category_id = data.category_id || '';
    this.ingredients = Array.isArray(data.ingredients) ? data.ingredients.filter(Boolean) : [];
    this.nutrition_info = data.nutrition_info || {};
    this.allergens = data.allergens || [];
    this.tags = Array.isArray(data.tags) ? data.tags.filter(Boolean) : [];
  }

  get spiceLevelColor() {
    const levels = {
      'mild': '#27ae60',
      'medium': '#f39c12',
      'mediumhot': '#e67e22',
      'hot': '#e74c3c',
      'spicy': '#c0392b',
      'sweet': '#9b59b6'
    };
    return levels[this.spice_level?.toLowerCase()] || '#f39c12';
  }

  get spiceLevelClass() {
    return this.spice_level?.toLowerCase().replace(' ', '') || 'medium';
  }

  matchesSearch(searchTerm) {
    if (!searchTerm) return true;
    
    const term = searchTerm.toLowerCase();
    return (
      this.name.toLowerCase().includes(term) ||
      this.description.toLowerCase().includes(term) ||
      this.food_type_name.toLowerCase().includes(term) ||
      this.category_name.toLowerCase().includes(term) ||
      this.ingredients.some(ingredient => 
        ingredient && ingredient.toLowerCase().includes(term)
      ) ||
      this.tags.some(tag => tag.toLowerCase().includes(term))
    );
  }

  static createFromArray(dataArray) {
    return dataArray.map(data => new MenuItem(data));
  }
}


class MenuStats {
  constructor(categories = []) {
    this.categories = categories;
  }

  get totalItems() {
    return this.getAllItems().length;
  }

  get totalCategories() {
    return this.categories.length;
  }

  get availableItems() {
    return this.getAllItems().filter(item => item.is_available).length;
  }

  get chefSpecials() {
    return this.getAllItems().filter(item => item.is_chef_special).length;
  }

  get popularItems() {
    return this.getAllItems().filter(item => item.is_popular).length;
  }

  getAllItems() {
    const allItems = [];
    this.categories.forEach(category => {
      if (category.items && Array.isArray(category.items)) {
        allItems.push(...category.items);
      }
    });
    return allItems;
  }

  getFoodTypes() {
    const allItems = this.getAllItems();
    const foodTypes = [...new Set(allItems.map(item => item.food_type_name).filter(Boolean))];
    return foodTypes.sort();
  }

  getCategoryStats() {
    return this.categories.map(category => ({
      name: category.name,
      display_name: category.display_name,
      total: category.items.length,
      available: category.availableItems.length,
      icon: category.icon
    }));
  }
}

// Enhanced Badge Components
const ChefSpecialBadge = React.memo(() => {
  return (
    <div className="badge chef-badge-enhanced">
      <span className="badge-icon">👑</span>
      <span className="badge-text">Chef's Special</span>
      <div className="badge-glow"></div>
    </div>
  );
});

const PopularBadge = React.memo(() => {
  return (
    <div className="badge popular-badge-enhanced">
      <span className="badge-icon">🔥</span>
      <span className="badge-text">Popular</span>
      <div className="badge-glow"></div>
    </div>
  );
});

// Sub-components for better organization
const MenuItemCard = React.memo(({ 
  item, 
  onItemClick, 
  animationDelay,
  fallbackImage 
}) => {
  const itemRef = useRef(null);
  const [imageError, setImageError] = useState(false);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);


  const handleClick = useCallback(() => {
    if (item && onItemClick) {
      onItemClick(item);
    }
  }, [item, onItemClick]);

  // Safe item access with fallbacks
  const safeItem = item || {};
  const safeName = safeItem.name || 'Unnamed Item';
  const safeDescription = safeItem.description || '';
  const safeCookingTime = safeItem.cooking_time || '15-20';
  const safeSpiceLevel = safeItem.spice_level || 'medium';
  const safeFoodType = safeItem.food_type_name || 'Main Course';
  const safeIngredients = Array.isArray(safeItem.ingredients) ? safeItem.ingredients : [];
  const safeTags = Array.isArray(safeItem.tags) ? safeItem.tags : [];

  // Function to get tag class based on tag content
  const getTagClass = (tag) => {
    if (!tag) return '';
    
    const tagLower = tag.toLowerCase();
    if (tagLower.includes('popular')) return 'popular';
    if (tagLower.includes('chef') || tagLower.includes('special')) return 'chef-special';
    if (tagLower.includes('veg') || tagLower === 've' || tagLower === 'vegetarian') return 'vegetarian';
    if (tagLower.includes('non') || tagLower.includes('non-veg')) return 'non-veg';
    if (tagLower.includes('spicy') || tagLower.includes('hot')) return 'spicy';
    if (tagLower.includes('bestseller')) return 'bestseller';
    if (tagLower.includes('new')) return 'new';
    if (tagLower.includes('seasonal')) return 'seasonal';
    return '';
  };

  return (
    <div
      ref={itemRef}
      className={`menu-item-card animated-item ${!safeItem.is_available ? "unavailable" : ""}`}
      style={{ animationDelay: `${animationDelay || 0}s` }}
      onClick={handleClick}
    >
      <div className="item-image-container">
        <img
          src={imageError ? fallbackImage : (safeItem.image || fallbackImage)}
          alt={safeName}
          className="item-image"
          onError={handleImageError}
          loading="lazy"
        />
        <div className="item-badges">
          {safeItem.is_chef_special && <ChefSpecialBadge />}
          {safeItem.is_popular && <PopularBadge />}
          {!safeItem.is_available && (
            <div className="badge unavailable-badge">⏳ Unavailable</div>
          )}
        </div>
        <div className="item-overlay">
          <button className="view-details-btn">
            View Details
          </button>
        </div>
      </div>

      <div className="item-content">
        <div className="item-header">
          <h3 className="item-name">{safeName}</h3>
        </div>

        <p className="item-description">{safeDescription}</p>

        {/* Enhanced Tags Section */}
        {safeTags.length > 0 && (
          <div className="item-tags">
            <div className="tags-container">
              {safeTags.slice(0, 4).map((tag, index) => (
                tag && (
                  <span key={index} className={`item-tag ${getTagClass(tag)}`}>
                    {tag}
                  </span>
                )
              ))}
              {safeTags.length > 4 && (
                <span className="tag-more">
                  +{safeTags.length - 4} more
                </span>
              )}
            </div>
          </div>
        )}

        <div className="item-meta">
          <div className="meta-item">
            <span className="meta-icon">⏱️</span>
            <span>{safeCookingTime} mins</span>
          </div>
          <div className="meta-item">
            <span className="meta-icon">🌶️</span>
            <span className={`spice-level ${safeItem.spiceLevelClass}`}>
              {safeSpiceLevel}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-icon">📦</span>
            <span>{safeFoodType}</span>
          </div>
        </div>

        {safeIngredients.length > 0 && (
          <div className="item-ingredients">
            <div className="ingredients-preview">
              {safeIngredients.slice(0, 2).map((ingredient, index) => (
                ingredient && (
                  <span key={index} className="ingredient-tag">
                    {ingredient}
                  </span>
                )
              ))}
              {safeIngredients.length > 2 && (
                <span className="ingredient-more">
                  +{safeIngredients.length - 2} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

const ItemModal = React.memo(({ 
  item, 
  isOpen, 
  onClose, 
  onShare,
  fallbackImage 
}) => {
  const [imageError, setImageError] = useState(false);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  const handleShare = useCallback(() => {
    if (item && onShare) {
      onShare(item.name || 'this item');
    }
  }, [item, onShare]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  }, [onClose]);

  // Safe item access with fallbacks
  const safeItem = item || {};
  const safeName = safeItem.name || 'Unnamed Item';
  const safeDescription = safeItem.detailed_description || safeItem.description || '';
  const safeCookingTime = safeItem.cooking_time || '15-20';
  const safeSpiceLevel = safeItem.spice_level || 'medium';
  const safeCategory = safeItem.category_name || '';
  const safeFoodType = safeItem.food_type_name || 'Main Course';
  const safeIngredients = Array.isArray(safeItem.ingredients) ? safeItem.ingredients : [];
  const safeTags = Array.isArray(safeItem.tags) ? safeItem.tags : [];

  // Function to get tag class based on tag content
  const getTagClass = (tag) => {
    if (!tag) return '';
    
    const tagLower = tag.toLowerCase();
    if (tagLower.includes('popular')) return 'popular';
    if (tagLower.includes('chef') || tagLower.includes('special')) return 'chef-special';
    if (tagLower.includes('veg') || tagLower === 've' || tagLower === 'vegetarian') return 'vegetarian';
    if (tagLower.includes('non') || tagLower.includes('non-veg')) return 'non-veg';
    if (tagLower.includes('spicy') || tagLower.includes('hot')) return 'spicy';
    if (tagLower.includes('bestseller')) return 'bestseller';
    if (tagLower.includes('new')) return 'new';
    if (tagLower.includes('seasonal')) return 'seasonal';
    return '';
  };

  // Don't render if no item
  if (!item) {
    return null;
  }

  return (
    <div
      className={`modal-overlay ${isOpen ? "active" : ""}`}
      onClick={handleBackdropClick}
    >
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header-section">
          <div className="modal-header-content">
            <h2 className="modal-title">{safeName}</h2>
            <div className="modal-header-badges">
              {safeItem.is_chef_special && (
                <div className="badge chef-badge-enhanced modal-badge">
                  <span className="badge-icon">👑</span>
                  <span className="badge-text">Chef's Special</span>
                  <div className="badge-glow"></div>
                </div>
              )}
              {safeItem.is_popular && (
                <div className="badge popular-badge-enhanced modal-badge">
                  <span className="badge-icon">🔥</span>
                  <span className="badge-text">Popular</span>
                  <div className="badge-glow"></div>
                </div>
              )}
            </div>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <span>×</span>
          </button>
        </div>

        <div className="modal-body">
          <div className="modal-image-container">
            <img
              src={imageError ? fallbackImage : (safeItem.image || fallbackImage)}
              alt={safeName}
              className="modal-main-image"
              onError={handleImageError}
            />
            {!safeItem.is_available && (
              <div className="modal-unavailable-overlay">
                <span className="modal-unavailable-text">Currently Unavailable</span>
              </div>
            )}
          </div>

          <div className="modal-details-container">
            <div className="modal-description-section">
              <p className="modal-description-text">
                {safeDescription}
              </p>
            </div>

            {/* Enhanced Tags in Modal */}
            {safeTags.length > 0 && (
              <div className="modal-tags-section">
                <h4 className="tags-title">
                  <span className="tags-icon">🏷️</span>
                  Dish Tags
                </h4>
                <div className="modal-tags-grid">
                  {safeTags.map((tag, index) => (
                    tag && (
                      <div
                        key={index}
                        className={`modal-tag-chip ${getTagClass(tag)}`}
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        {tag}
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}

            <div className="modal-info-cards">
              <div className="info-card">
                <div className="info-card-icon">⏱️</div>
                <div className="info-card-content">
                  <div className="info-card-label">Cooking Time</div>
                  <div className="info-card-value">{safeCookingTime} minutes</div>
                </div>
              </div>

              <div className="info-card">
                <div className="info-card-icon">🌶️</div>
                <div className="info-card-content">
                  <div className="info-card-label">Spice Level</div>
                  <div className="info-card-value">
                    <span 
                      className={`spice-indicator ${safeItem.spiceLevelClass}`}
                      style={{ borderColor: safeItem.spiceLevelColor }}
                    >
                      {safeSpiceLevel}
                    </span>
                  </div>
                </div>
              </div>

              <div className="info-card">
                <div className="info-card-icon">📂</div>
                <div className="info-card-content">
                  <div className="info-card-label">Category</div>
                  <div className="info-card-value">{safeCategory}</div>
                </div>
              </div>

              <div className="info-card">
                <div className="info-card-icon">🥘</div>
                <div className="info-card-content">
                  <div className="info-card-label">Food Type</div>
                  <div className="info-card-value">{safeFoodType}</div>
                </div>
              </div>
            </div>

            {safeIngredients.length > 0 && (
              <div className="ingredients-section">
                <h4 className="ingredients-title">
                  <span className="ingredients-icon">🎯</span>
                  Key Ingredients
                </h4>
                <div className="ingredients-grid">
                  {safeIngredients.map((ingredient, index) => (
                    ingredient && (
                      <div
                        key={index}
                        className="ingredient-chip"
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        {ingredient}
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}

            <div className="modal-actions-section">
              <button className="btn-secondary" onClick={onClose}>
                <span className="btn-icon">←</span>
                Back to Menu
              </button>
              <button
                className={`btn-primary ${!safeItem.is_available ? "disabled" : ""}`}
                onClick={handleShare}
                disabled={!safeItem.is_available}
              >
                <span className="btn-icon">📱</span>
                {safeItem.is_available
                  ? "Share on WhatsApp"
                  : "Currently Unavailable"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const CategoryNavigation = React.memo(({
  categories,
  activeCategory,
  onCategoryChange,
  onScrollToCategory,
  getAllItems,
  isScrolled
}) => {
  const handleCategoryClick = useCallback((categoryName) => {
    if (onCategoryChange) {
      onCategoryChange(categoryName);
    }
    if (onScrollToCategory && categoryName !== 'all') {
      onScrollToCategory(categoryName);
    }
  }, [onCategoryChange, onScrollToCategory]);

  const handleAllItemsClick = useCallback(() => {
    if (onCategoryChange) {
      onCategoryChange("all");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [onCategoryChange]);

  const itemsCount = getAllItems ? getAllItems().length : 0;

  return (
    <div className={`category-nav-container ${isScrolled ? "scrolled" : ""}`}>
      <div className="category-nav-scroll">
        <div className="category-nav">
          <button
            className={`category-btn ${activeCategory === "all" ? "active" : ""}`}
            onClick={handleAllItemsClick}
          >
            <span className="category-icon">🍴</span>
            <span className="category-name">All Items</span>
            <span className="item-count">
              ({itemsCount})
            </span>
          </button>

          {categories.map((category) => (
            <button
              key={category.name}
              className={`category-btn ${activeCategory === category.name ? "active" : ""}`}
              onClick={() => handleCategoryClick(category.name)}
            >
              <span className="category-icon">{category.icon}</span>
              <span className="category-name">{category.display_name || category.name}</span>
              <span className="item-count">({category.items.length})</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
});

const SearchAndFilter = React.memo(({
  searchTerm,
  onSearchChange,
  activeFoodType,
  onFoodTypeChange,
  availableFoodTypes
}) => {
  const handleSearchChange = useCallback((e) => {
    if (onSearchChange) {
      onSearchChange(e.target.value);
    }
  }, [onSearchChange]);

  const handleClearSearch = useCallback(() => {
    if (onSearchChange) {
      onSearchChange("");
    }
  }, [onSearchChange]);

  const handleFoodTypeChange = useCallback((e) => {
    if (onFoodTypeChange) {
      onFoodTypeChange(e.target.value);
    }
  }, [onFoodTypeChange]);

  return (
    <div className="search-filter-container animated-slide-up">
      <div className="search-wrapper">
        <input
          type="text"
          placeholder="🔍 Search dishes, ingredients, or categories..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="search-input"
        />
        {searchTerm && (
          <button
            className="clear-search"
            onClick={handleClearSearch}
          >
            ✕
          </button>
        )}
      </div>

      <div className="filter-controls">
        <select
          value={activeFoodType}
          onChange={handleFoodTypeChange}
          className="filter-select"
        >
          <option value="all">All Food Types</option>
          {availableFoodTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>
    </div>
  );
});

// Header Component
const MenuHeader = React.memo(({ restaurantName, tagline, onBackToHome }) => {
  return (
    <header className="menu-header">
      <div className="header-background">
        <div className="header-overlay"></div>
      </div>
      <div className="header-content">
      <br />
      <br />
      </div>
    </header>
  );
});

// Main Component
const FullMenu = ({ 
  onBackToHome, 
  categories = [], 
  menuItems = [], 
  foodTypes = [], 
  apiError, 
  isMobile 
}) => {
  // State
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeFoodType, setActiveFoodType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Refs
  const categoryRefs = useRef({});
  const observerRef = useRef(null);

  // Models - with safe data transformation
  const menuCategories = useMemo(() => {
    if (!Array.isArray(categories)) return [];
    return categories
      .filter(cat => cat && typeof cat === 'object')
      .map(cat => new MenuCategory(cat))
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
  }, [categories]);

  const menuStats = useMemo(() => new MenuStats(menuCategories), [menuCategories]);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Intersection Observer for animations
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animated-visible');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Memoized data
  const availableCategories = useMemo(() => 
    menuCategories.filter(cat => cat.items && cat.items.length > 0),
    [menuCategories]
  );

  // Fixed filter function
  const applyFilters = useCallback((items) => {
    let filtered = [...items];

    // Filter by category - fixed logic
    if (activeCategory !== 'all') {
      filtered = filtered.filter(item => {
        // Check both category_name and category_id for compatibility
        return item.category_name === activeCategory || 
               item.category_id === activeCategory ||
               // Also check if item belongs to a category with this name
               menuCategories.some(cat => 
                 cat.name === activeCategory && 
                 cat.items.some(catItem => catItem.id === item.id)
               );
      });
    }

    // Filter by food type
    if (activeFoodType !== 'all') {
      filtered = filtered.filter(item => 
        item.food_type_name && 
        item.food_type_name.toLowerCase() === activeFoodType.toLowerCase()
      );
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(item => item.matchesSearch(term));
    }

    // Sort items by name
    filtered.sort((a, b) => a.name.localeCompare(b.name));

    return filtered;
  }, [activeCategory, activeFoodType, searchTerm, menuCategories]);

  const filteredItems = useMemo(() => {
    if (!menuStats) return [];
    const allItems = menuStats.getAllItems();
    return applyFilters(allItems);
  }, [menuStats, applyFilters]);

  // Event Handlers
  const openModal = useCallback((item) => {
    if (!item || !item.is_available) return;
    setSelectedItem(item);
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setTimeout(() => {
      setSelectedItem(null);
      document.body.style.overflow = "unset";
    }, 300);
  }, []);

  const shareOnWhatsApp = useCallback((productName) => {
    const message = `I'm interested in ${productName} from Ali Darbar. Can you tell me more about it?`;
    const url = `https://wa.me/+917030808025?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  }, []);

  const scrollToCategory = useCallback((categoryName) => {
    const element = categoryRefs.current[categoryName];
    if (element) {
      const offset = 120;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });
    }
  }, []);

  const handleSearchChange = useCallback((term) => {
    setSearchTerm(term);
  }, []);

  const handleCategoryChange = useCallback((category) => {
    setActiveCategory(category);
    // Reset to top when changing categories
    if (category === 'all') {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const handleFoodTypeChange = useCallback((foodType) => {
    setActiveFoodType(foodType);
  }, []);

  // Reset scroll on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="full-menu-page">
      {/* Menu Header */}
      <MenuHeader 
        restaurantName="Ali Darbar"
        tagline="Mini Family Restaurant"
        onBackToHome={onBackToHome}
      />

      {/* Animated Background Elements */}
      <div className="animated-bg-elements">
        <div className="floating-spice floating-spice-1">🌶️</div>
        <div className="floating-spice floating-spice-2">🍛</div>
        <div className="floating-spice floating-spice-3">🍗</div>
        <div className="floating-spice floating-spice-4">🥘</div>
      </div>

      <section id="full-menu" className="section full-menu-section">
        <div className="container">
          {apiError && (
            <div className="api-error animated-fade-in">
              <p>{apiError}</p>
              <button
                className="btn btn-outline"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          )}

          <div className="full-menu-content">
            {/* Enhanced Title Section */}
            <div className="section-title animated-title">
              <div className="title-decoration">
                <div className="decoration-line"></div>
                <div className="decoration-icon">🍽️</div>
                <div className="decoration-line"></div>
              </div>
              <h1 className="title-main">
                <span className="title-word title-word-1">Complete</span>
                <span className="title-word title-word-2">Menu</span>
              </h1>
              <p className="title-subtitle">
                Explore all our {menuStats.totalItems} delicious dishes across {availableCategories.length} categories
              </p>
              <div className="menu-stats">
                <div className="stat-item">
                  <span className="stat-number">{menuStats.availableItems}</span>
                  <span className="stat-label">Available</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{menuStats.popularItems}</span>
                  <span className="stat-label">Popular</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{menuStats.chefSpecials}</span>
                  <span className="stat-label">Chef Specials</span>
                </div>
              </div>
            </div>

            {/* Search and Filter */}
            <SearchAndFilter
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              activeFoodType={activeFoodType}
              onFoodTypeChange={handleFoodTypeChange}
              availableFoodTypes={menuStats.getFoodTypes()}
            />

            {/* Category Navigation */}
            <CategoryNavigation
              categories={availableCategories}
              activeCategory={activeCategory}
              onCategoryChange={handleCategoryChange}
              onScrollToCategory={scrollToCategory}
              getAllItems={() => menuStats.getAllItems()}
              isScrolled={isScrolled}
            />

            {/* Search Results Info */}
            {(searchTerm || activeFoodType !== "all" || activeCategory !== "all") && (
              <div className="search-results-info animated-fade-in">
                <div className="results-content">
                  <p>
                    {filteredItems.length > 0 ? "🎉 Found" : "🔍 No"} <strong>{filteredItems.length}</strong> item
                    {filteredItems.length !== 1 ? "s" : ""}
                    {activeCategory !== "all" &&
                      ` in ${menuCategories.find(cat => cat.name === activeCategory)?.display_name || activeCategory}`}
                    {activeFoodType !== "all" &&
                      ` (${activeFoodType})`}
                    {searchTerm && ` for "${searchTerm}"`}
                  </p>
                  {(searchTerm || activeFoodType !== "all") && (
                    <button
                      className="btn btn-outline small"
                      onClick={() => {
                        setSearchTerm("");
                        setActiveFoodType("all");
                      }}
                    >
                      ✕ Clear Filters
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* All Categories View */}
            {activeCategory === "all" && !searchTerm && activeFoodType === "all" && (
              <>
                {availableCategories.map((category, categoryIndex) => (
                  <div
                    key={category.name}
                    className="category-section animated-category"
                    id={category.name}
                    ref={(el) => {
                      if (el && category.name) {
                        categoryRefs.current[category.name] = el;
                      }
                    }}
                  >
                    <div className="category-header">
                      <h2 className="category-title">
                        <span className="title-text">
                          {category.display_name || category.name}
                        </span>
                        <span className="title-underline"></span>
                      </h2>
                      {category.description && (
                        <p className="category-description">
                          {category.description}
                        </p>
                      )}
                      <div className="category-stats">
                        <span className="category-item-count">
                          {category.items.length} item{category.items.length !== 1 ? 's' : ''}
                        </span>
                        <span className="category-available-count">
                          {category.availableItems.length} available
                        </span>
                      </div>
                    </div>
                    <div className="menu-grid">
                      {category.items.map((item, itemIndex) => (
                        <MenuItemCard
                          key={item.id}
                          item={item}
                          onItemClick={openModal}
                          animationDelay={itemIndex * 0.1}
                          fallbackImage={alidarbar}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Filtered Items View */}
            {(activeCategory !== "all" || searchTerm || activeFoodType !== "all") && (
              <div className="category-section animated-fade-in">
                {filteredItems.length > 0 ? (
                  <div className="menu-grid">
                    {filteredItems.map((item, index) => (
                      <MenuItemCard
                        key={item.id}
                        item={item}
                        onItemClick={openModal}
                        animationDelay={index * 0.1}
                        fallbackImage={alidarbar}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="no-items-found">
                    <div className="no-items-icon">🍽️</div>
                    <h3>No dishes found matching your criteria.</h3>
                    <p>Try adjusting your filters or search terms.</p>
                    <div className="no-items-actions">
                      <button
                        className="btn btn-outline"
                        onClick={() => {
                          setSearchTerm("");
                          setActiveFoodType("all");
                          setActiveCategory("all");
                        }}
                      >
                        🔄 Clear All Filters
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={() => setActiveCategory("all")}
                      >
                        👀 View All Items
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Item Modal - Only render when selectedItem exists */}
        {selectedItem && (
          <ItemModal
            item={selectedItem}
            isOpen={isModalOpen}
            onClose={closeModal}
            onShare={shareOnWhatsApp}
            fallbackImage={alidarbar}
          />
        )}
      </section>
    </div>
  );
};

export default FullMenu;